import React from 'react';
import useAuthContext from '../../hooks/useAuthContext';
import { Title, Loader, ReloadIconButton } from '../../elements';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE_MIDDLE, LoaderSize } from '../../constants/constants';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  Chip,
  Container,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import { CreateVaccinationRuleModal } from '../../components';
import { AnimalType } from '../../models/animal';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import { HealthSystemDomainVaccinationRulesResultVaccinationRuleViewModel } from '../../api/HealthSystemApi';

const VaccinationRules: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const confirmDialog = useConfirmation();

  const apiService = new ApiService();

  const allowEdit = state.role === UserRole.manager;

  const [vaccinationRules, setVaccinationRules] = React.useState<
    HealthSystemDomainVaccinationRulesResultVaccinationRuleViewModel[]
  >([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [createVaccinationRuleModalOpened, setCreateVaccinationRuleModalOpened] = React.useState(false);

  const loadFarmVaccinationRules = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;

    apiService.api.vaccinationRules
      .vaccinationRulesVaccinationRulesSearch({ Page: searchPage, Size: rowsPerPage })
      .then((result) => {
        setVaccinationRules(result.data.data);
        setTotal(result.data.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadFarmVaccinationRules();
      loadData();
    }
  }, [loaded]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE_MIDDLE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const handleRemoveVaccinationRule = (
    vaccinationRule: HealthSystemDomainVaccinationRulesResultVaccinationRuleViewModel
  ) => {
    confirmDialog({
      dialogId: 'delete-vaccination-rule-dialog',
      title: t('f-vaccination-rule-del'),
      message: t('f-vaccination-rule-del-confirm'),
      alertSeverity: 'warning'
    }).then(() => {
      apiService.api.vaccinationRules
        .vaccinationRulesRemoveVaccinationRule({ id: vaccinationRule.id })
        .then(() => {
          notifications.success(t('f-vaccination-rule-del-ok'));
          setLoaded(false);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    });
  };

  const handleCreateVaccinationRuleClose = (result: boolean) => {
    if (result) {
      setLoaded(false);
    }
    setCreateVaccinationRuleModalOpened(false);
  };

  const renderAnimalTypeCell = (vaccinationRule: HealthSystemDomainVaccinationRulesResultVaccinationRuleViewModel) => {
    return (
      <>
        {vaccinationRule.isPregnant && (
          <Tooltip arrow title={t('f-animal-preg-only')}>
            <IconButton size="small" color="default" sx={V4_ICON_BUTTON_SMALL}>
              <Icon>{'eco'}</Icon>
            </IconButton>
          </Tooltip>
        )}

        {vaccinationRule.isLactating && (
          <Tooltip arrow title={t('f-animal-lact-only')}>
            <IconButton size="small" color="default" sx={V4_ICON_BUTTON_SMALL}>
              <Icon>{'grain'}</Icon>
            </IconButton>
          </Tooltip>
        )}

        {AnimalType[vaccinationRule.animalType]}
      </>
    );
  };

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Grid container direction="row" sx={styles.paperHeader}>
              <Grid item container md={6} xs={10} spacing={1}>
                <Grid item>
                  <Icon fontSize="large" color="primary">
                    {'rule'}
                  </Icon>
                </Grid>
                <Grid item>
                  <Title>{t('f-vaccination-rules')}</Title>
                </Grid>
                <Grid item>
                  <Chip label={total} variant="outlined" />
                </Grid>
              </Grid>

              <Grid item container sx={styles.justifyRight} md={6} xs={12}>
                <Grid item>
                  <ReloadIconButton action={() => setLoaded(false)} />
                </Grid>

                {!allowEdit && (
                  <Grid item>
                    <Tooltip title={t('f-com-create-new')} arrow={true}>
                      <IconButton
                        id="createVaccinationRuleBtn"
                        sx={{ ...V4_ICON_BUTTON_SMALL, ...styles.withMarginLeft }}
                        color="primary"
                        size="small"
                        onClick={() => setCreateVaccinationRuleModalOpened(true)}
                      >
                        <Icon>{'add_circle'}</Icon>
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

            {loaded && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TableContainer component={Paper} elevation={1} sx={styles.mt2}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell align="right">{t('f-com-animal-type')}</TableCell>
                          <TableCell align="right">{t('f-tasks-age-from')}</TableCell>
                          <TableCell>{t('f-tasks-age-to')}</TableCell>
                          <TableCell align="right">{t('f-com-supply')}</TableCell>
                          <TableCell>{t('f-com-dose')}</TableCell>

                          <TableCell padding="checkbox" />
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {vaccinationRules.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell align="right">{renderAnimalTypeCell(row)}</TableCell>
                            <TableCell align="right">{row.ageFrom}</TableCell>
                            <TableCell>{row.ageTo}</TableCell>
                            <TableCell align="right">{row.medicalSupply.number}</TableCell>
                            <TableCell>{row.dose.toFixed(2) || '-'}</TableCell>
                            <TableCell>
                              {allowEdit && (
                                <Tooltip arrow title={t('f-com-remove')}>
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() => handleRemoveVaccinationRule(row)}
                                    sx={V4_ICON_BUTTON_SMALL}
                                  >
                                    <Icon>{'delete_outline'}</Icon>
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>

                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                            colSpan={6}
                            count={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Container>

      <CreateVaccinationRuleModal
        opened={createVaccinationRuleModalOpened}
        onModalAction={handleCreateVaccinationRuleClose}
      />
    </>
  );
};

export default VaccinationRules;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    },
    mt2: {
      marginTop: theme.spacing(2)
    }
  }) as const;
